/* eslint-disable react/no-unescaped-entities */
/**
 * NegociationDossiers.jsx — Liste de mes dossiers de négociation (créateur).
 *
 * Charte stricte (`/app/memory/NEGOCIATION_CHARTER.md`).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api";
import { Plus, ArrowRight, Loader2, Inbox, ShieldCheck } from "lucide-react";

const COMPLIANCE = (
  "Confilex est un outil de structuration documentaire et de formalisation " +
  "d'informations fournies par les utilisateurs. Confilex ne fournit aucune " +
  "consultation juridique, n'analyse pas les droits des parties, ne vérifie " +
  "pas la validité des créances et ne rédige aucun acte juridique."
);

export default function NegociationDossiers() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/negotiation/cases");
        setCases(data.cases || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar variant="light" />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="negociation-dossiers">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961] font-medium mb-2">CONFILEX · Négociation</p>
            <h1 className="font-serif text-3xl text-[#0B3A2A]">Mes dossiers de négociation</h1>
            <p className="mt-2 text-sm text-slate-600">
              Tous vos échanges amiables structurés, horodatés et archivés.
            </p>
          </div>
          <Button asChild className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white" data-testid="btn-new-case">
            <Link to="/negociation/nouveau"><Plus className="w-4 h-4 mr-1.5" /> Ouvrir un dossier</Link>
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-16"><Loader2 className="w-6 h-6 text-[#C9A961] mx-auto animate-spin" /></div>
        ) : cases.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="grid md:grid-cols-2 gap-4" data-testid="dossiers-list">
            {cases.map(c => <CaseCard key={c.id} c={c} />)}
          </ul>
        )}

        <div className="mt-10 p-4 rounded-lg bg-[#FAF7F1] border border-[#C9A961]/30 text-xs text-[#0B3A2A] flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-[#C9A961] mt-0.5 shrink-0" />
          <p><b>Mention de conformité :</b> {COMPLIANCE}</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function CaseCard({ c }) {
  const last = c.last_activity_at?.slice(0, 10);
  return (
    <li className="rounded-xl bg-white border border-slate-200 p-5 hover:border-[#C9A961] transition" data-testid={`case-${c.id}`}>
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FAF7F1] border border-[#C9A961]/30">
          {c.status_icon} {c.status_label}
        </span>
        <span className="ml-auto">Dernière activité : {last}</span>
      </div>
      <p className="font-serif text-lg text-[#0B3A2A] line-clamp-2 mb-1">{c.subject}</p>
      <p className="text-xs text-slate-600 mb-3">
        Entre <b>{c.initiator_party.name}</b> et <b>{c.opposing_party.name || c.opposing_party.organization || "—"}</b>
      </p>
      {c.amount_eur != null && (
        <p className="text-xs text-slate-500 mb-3">
          Montant déclaré : <b className="text-[#0B3A2A]">{Number(c.amount_eur).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}</b>
        </p>
      )}
      <Button asChild size="sm" variant="outline" className="w-full">
        <Link to={`/negociation/dossier/${c.id}`}>Ouvrir le dossier <ArrowRight className="w-3.5 h-3.5 ml-1" /></Link>
      </Button>
    </li>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-12 text-center">
      <Inbox className="w-10 h-10 text-[#C9A961] mx-auto mb-3" />
      <h2 className="font-serif text-xl text-[#0B3A2A] mb-2">Aucun dossier pour le moment</h2>
      <p className="text-sm text-slate-600 max-w-md mx-auto mb-6">
        Démarrez une démarche amiable en quelques minutes : identité des parties,
        objet, montant, échéancier souhaité.
      </p>
      <Button asChild className="bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white">
        <Link to="/negociation/nouveau"><Plus className="w-4 h-4 mr-1.5" /> Ouvrir mon premier dossier</Link>
      </Button>
    </div>
  );
}
